import React, { useContext, useEffect, useState } from 'react';
import { Card, Table, Badge, Spinner, Alert, Form, Button } from 'react-bootstrap';
import { AppContext } from '../AppContext';
import { tre } from '../trebus';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { FiCalendar, FiFilter, FiRefreshCw, FiDollarSign, FiChevronLeft, FiChevronRight, FiUser } from 'react-icons/fi';

export default function DriverPayments() {
    const [payments, setPayments] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [drivers, setDrivers] = useState([]);
    const [selectedDriver, setSelectedDriver] = useState('');
    const [loadingDrivers, setLoadingDrivers] = useState(true);
    const itemsPerPage = 30;
    const context = useContext(AppContext);

    // Fetch drivers (users with DRIVER role)
    const fetchDrivers = () => {
        if (!context.idCompany) return;

        setLoadingDrivers(true);
        tre.api('GET', `/companies/${context.idCompany}/users`, {
            context: context,
            params: {
                role: 'DRIVER',
                rows: 100
            },
            success: (response) => {
                if (response.data && Array.isArray(response.data)) {
                    setDrivers(response.data);
                } else {
                    setDrivers([]);
                }
                setLoadingDrivers(false);
            },
            error: () => {
                setDrivers([]);
                setLoadingDrivers(false);
            }
        });
    };

    const formatDateForApi = (date) => {
        return moment(date).format('YYYY-MM-DD');
    };

    const fetchPayments = (page) => {
        setLoading(true);
        setError(null);

        let queryParams = `page=${page}&per_page=${itemsPerPage}`;

        if (selectedDate) {
            queryParams += `&updated_at=${formatDateForApi(selectedDate)}`;
        }

        if (selectedDriver) {
            queryParams += `&collector_id=${selectedDriver}`;
        }

        tre.api('GET', `/drivers/payments?${queryParams}`, {
            context: context,
            success: (response) => {
                if (response.data && Array.isArray(response.data)) {
                    setPayments(response.data);
                    const totalRecords = response.total || response.data.length;
                    setTotal(totalRecords);
                    setTotalPages(Math.ceil(totalRecords / itemsPerPage));
                } else {
                    setPayments([]);
                    setError('Formato de datos inválido recibido del servidor');
                }
                setLoading(false);
            },
            error: (err) => {
                setError(err.error || 'Error al cargar los pagos de conductores');
                setLoading(false);
            }
        });
    };

    useEffect(() => {
        if (context.idCompany) {
            fetchDrivers();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [context.idCompany]);

    useEffect(() => {
        if (context.idCompany) {
            fetchPayments(currentPage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [context.idCompany, currentPage, selectedDate, selectedDriver]);

    const handleDateChange = (date) => {
        setSelectedDate(date);
        setCurrentPage(1);
    };

    const handleDriverChange = (e) => {
        setSelectedDriver(e.target.value);
        setCurrentPage(1);
    };

    const clearFilters = () => {
        setSelectedDate(new Date());
        setSelectedDriver('');
        setCurrentPage(1);
    };

    const goToPage = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    // Calculate total for displayed payments
    const displayedTotal = payments.reduce((sum, p) => sum + (typeof p.price === 'number' ? p.price : 0), 0);

    // -----| Render |-----
    return (
        <div className="container py-4">
            {/* Header */}
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div>
                    <h4 className="mb-1" style={{ color: '#202124', fontWeight: 500 }}>
                        <FiDollarSign className="me-2" style={{ color: '#1a73e8' }} />
                        Cobros de Conductores
                    </h4>
                    <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                        {total} cobros en total
                    </p>
                </div>
                <Button variant="outline-secondary" onClick={() => fetchPayments(currentPage)} disabled={loading}>
                    <FiRefreshCw className={loading ? 'spin' : ''} />
                </Button>
            </div>

            {/* Filters - Inline style */}
            <div className="d-flex flex-wrap align-items-center gap-3 mb-4 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                {/* Date picker */}
                <div style={{ minWidth: '180px' }}>
                    <div className="d-flex align-items-center gap-2">
                        <FiCalendar style={{ color: '#5f6368' }} />
                        <DatePicker
                            selected={selectedDate}
                            onChange={handleDateChange}
                            dateFormat="dd/MM/yyyy"
                            className="form-control"
                            placeholderText="Seleccionar fecha"
                            style={{ backgroundColor: 'white' }}
                        />
                    </div>
                </div>

                {/* Driver selector */}
                <div style={{ minWidth: '200px' }}>
                    <div className="d-flex align-items-center gap-2">
                        <FiUser style={{ color: '#5f6368' }} />
                        {loadingDrivers ? (
                            <div className="d-flex align-items-center">
                                <Spinner animation="border" size="sm" className="me-2" style={{ color: '#1a73e8' }} />
                                <span className="text-muted" style={{ fontSize: '14px' }}>Cargando...</span>
                            </div>
                        ) : (
                            <Form.Select
                                value={selectedDriver}
                                onChange={handleDriverChange}
                                style={{ backgroundColor: 'white' }}
                            >
                                <option value="">Todos los conductores</option>
                                {drivers.map(driver => (
                                    <option key={driver.id} value={driver.id}>
                                        {driver.name} {driver.surname || ''}
                                    </option>
                                ))}
                            </Form.Select>
                        )}
                    </div>
                </div>

                {/* Clear filters */}
                {(selectedDriver || (selectedDate && moment(selectedDate).format('YYYY-MM-DD') !== moment().format('YYYY-MM-DD'))) && (
                    <Button
                        variant="link"
                        size="sm"
                        onClick={clearFilters}
                        style={{ color: '#ea4335', textDecoration: 'none' }}
                    >
                        <FiFilter className="me-1" /> Limpiar
                    </Button>
                )}
            </div>

            {/* Table Card */}
            <Card>
                {/* Top Pagination Info */}
                {!loading && payments.length > 0 && (
                    <Card.Header className="d-flex flex-wrap justify-content-between align-items-center py-3 gap-3" style={{ backgroundColor: '#f8f9fa' }}>
                        <div className="d-flex align-items-center gap-4">
                            <span style={{ fontSize: '14px', color: '#5f6368' }}>
                                <strong style={{ color: '#202124' }}>{total}</strong> registros
                            </span>
                            <span style={{ fontSize: '14px', color: '#5f6368' }}>
                                Página <strong style={{ color: '#202124' }}>{currentPage}</strong> de <strong style={{ color: '#202124' }}>{totalPages}</strong>
                            </span>
                        </div>
                        {totalPages > 1 && (
                            <div className="d-flex gap-2">
                                <Button variant="outline-secondary" size="sm" disabled={currentPage <= 1} onClick={() => goToPage(1)}>
                                    Primera
                                </Button>
                                <Button variant="outline-secondary" size="sm" disabled={currentPage <= 1} onClick={() => goToPage(currentPage - 1)}>
                                    <FiChevronLeft className="me-1" /> Anterior
                                </Button>
                                <Button variant="outline-secondary" size="sm" disabled={currentPage >= totalPages} onClick={() => goToPage(currentPage + 1)}>
                                    Siguiente <FiChevronRight className="ms-1" />
                                </Button>
                                <Button variant="outline-secondary" size="sm" disabled={currentPage >= totalPages} onClick={() => goToPage(totalPages)}>
                                    Última
                                </Button>
                            </div>
                        )}
                    </Card.Header>
                )}

                <Card.Body className="p-0">
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" style={{ color: '#1a73e8' }} />
                            <p className="mt-3 text-muted">Cargando cobros...</p>
                        </div>
                    ) : error ? (
                        <Alert variant="danger" className="m-4" style={{ borderRadius: '8px' }}>
                            <p className="mb-2">{error}</p>
                            <Button variant="outline-danger" size="sm" onClick={() => fetchPayments(currentPage)}>
                                <FiRefreshCw className="me-1" /> Reintentar
                            </Button>
                        </Alert>
                    ) : payments.length > 0 ? (
                        <Table hover responsive className="mb-0">
                            <thead style={{ backgroundColor: '#f8f9fa' }}>
                                <tr>
                                    <th className="py-3 px-3" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>FECHA</th>
                                    <th className="py-3" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>CONCEPTO</th>
                                    <th className="py-3" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>USUARIO</th>
                                    <th className="py-3" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>CONDUCTOR</th>
                                    <th className="py-3 text-center" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>ESTADO</th>
                                    <th className="py-3 px-3 text-end" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>IMPORTE</th>
                                </tr>
                            </thead>
                            <tbody>
                                {payments.map((payment) => (
                                    <tr key={payment.id || `payment-${Math.random()}`}>
                                        <td className="py-3 px-3">
                                            <span className="text-muted" style={{ fontSize: '14px' }}>
                                                {payment.updated ? moment(payment.updated).format('DD/MM/YYYY HH:mm') : '-'}
                                            </span>
                                        </td>
                                        <td className="py-3">
                                            <span style={{ color: '#202124' }}>{payment.concept || '-'}</span>
                                        </td>
                                        <td className="py-3">
                                            <span style={{ color: '#202124' }}>
                                                {payment.user ? `${payment.user.name || ''} ${payment.user.surname || ''}`.trim() : '-'}
                                            </span>
                                        </td>
                                        <td className="py-3">
                                            {payment.collector ? (
                                                <div className="d-flex align-items-center">
                                                    <div
                                                        className="d-flex align-items-center justify-content-center rounded-circle me-2"
                                                        style={{
                                                            width: '28px',
                                                            height: '28px',
                                                            backgroundColor: '#e8f0fe',
                                                            color: '#1a73e8',
                                                            fontSize: '12px',
                                                            fontWeight: 600
                                                        }}
                                                    >
                                                        {(payment.collector.name || '?').charAt(0).toUpperCase()}
                                                    </div>
                                                    <span style={{ color: '#202124' }}>{payment.collector.name}</span>
                                                </div>
                                            ) : '-'}
                                        </td>
                                        <td className="py-3 text-center">
                                            <Badge
                                                bg={payment.is_paid === 1 ? 'success' : 'warning'}
                                                style={{ fontSize: '12px', padding: '6px 12px', fontWeight: 500 }}
                                            >
                                                {payment.is_paid === 1 ? 'Pagado' : 'Pendiente'}
                                            </Badge>
                                        </td>
                                        <td className="py-3 px-3 text-end">
                                            <span style={{ fontWeight: 600, color: '#202124' }}>
                                                {typeof payment.price === 'number' ? `${payment.price.toFixed(2)} €` : '-'}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot style={{ backgroundColor: '#f8f9fa' }}>
                                <tr>
                                    <td colSpan="5" className="py-3 px-3 text-end" style={{ fontWeight: 600 }}>Total:</td>
                                    <td className="py-3 px-3 text-end" style={{ fontWeight: 700, color: '#1a73e8', fontSize: '16px' }}>
                                        {displayedTotal.toFixed(2)} €
                                    </td>
                                </tr>
                            </tfoot>
                        </Table>
                    ) : (
                        <div className="text-center py-5">
                            <FiDollarSign size={48} className="mb-3" style={{ color: '#dadce0' }} />
                            <p className="text-muted">No hay cobros para los filtros seleccionados</p>
                        </div>
                    )}
                </Card.Body>

                {/* Bottom Pagination */}
                {payments.length > 0 && totalPages > 1 && (
                    <Card.Footer className="d-flex flex-wrap justify-content-between align-items-center py-3 gap-3" style={{ backgroundColor: '#f8f9fa' }}>
                        <span style={{ fontSize: '14px', color: '#5f6368' }}>
                            Página <strong style={{ color: '#202124' }}>{currentPage}</strong> de <strong style={{ color: '#202124' }}>{totalPages}</strong>
                        </span>
                        <div className="d-flex gap-2">
                            <Button variant="outline-secondary" size="sm" disabled={currentPage <= 1} onClick={() => goToPage(1)}>
                                Primera
                            </Button>
                            <Button variant="outline-secondary" size="sm" disabled={currentPage <= 1} onClick={() => goToPage(currentPage - 1)}>
                                <FiChevronLeft className="me-1" /> Anterior
                            </Button>
                            <Button variant="outline-secondary" size="sm" disabled={currentPage >= totalPages} onClick={() => goToPage(currentPage + 1)}>
                                Siguiente <FiChevronRight className="ms-1" />
                            </Button>
                            <Button variant="outline-secondary" size="sm" disabled={currentPage >= totalPages} onClick={() => goToPage(totalPages)}>
                                Última
                            </Button>
                        </div>
                    </Card.Footer>
                )}
            </Card>
        </div>
    );
}
